import { motion } from 'framer-motion'
import Button from '../ui/Button'
import { BalloonLayer } from '../ui/BalloonSVG'
import { scrollToId } from '../../utils/scrollTo'

const PHONE = '07775000'

const containerVariants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.2 } },
}

const itemVariants = {
  hidden: { opacity: 0, y: 32 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: 'easeOut' } },
}

export default function HeroBanner() {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #FF6BB5 0%, #7C3AFF 100%)' }}
    >
      {/* Decorative orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none" />

      {/* Floating balloon decorations */}
      <BalloonLayer opacity={0.40} />

      <div className="container-custom relative z-10 pt-28 pb-16">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-8 items-center">

          {/* ── Left column (text) ────────────────────────────────────────── */}
          <motion.div
            className="lg:col-span-3"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {/* Eyebrow */}
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/20 border border-white/30 text-white text-sm font-body font-bold tracking-wide backdrop-blur-sm">
                ✨ Cairo's #1 Children's Party Planner
              </span>
            </motion.div>

            {/* H1 */}
            <motion.h1
              variants={itemVariants}
              className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none mt-6 mb-5 text-balance"
            >
              Every birthday becomes a{' '}
              <span className="text-secondary">magical</span> world ✨
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="font-body text-lg md:text-xl text-white/90 max-w-lg leading-relaxed"
            >
              We plan, decorate &amp; entertain — you just enjoy the smile
            </motion.p>

            {/* CTA buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-4 mt-8"
            >
              <Button to="/contact" size="lg" variant="dark">
                🎉 Book Your Party
              </Button>
              <Button
                href={`tel:${PHONE}`}
                size="lg"
                variant="outline-white"
              >
                📞 {PHONE}
              </Button>
            </motion.div>

            {/* Stats row */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20"
            >
              {[
                { value: '500+', label: 'Happy Birthdays' },
                { value: '10+',  label: 'Years Experience' },
                { value: '100%', label: 'Satisfaction'     },
              ].map(({ value, label }) => (
                <div key={label}>
                  <p className="font-display text-3xl text-secondary leading-none">{value}</p>
                  <p className="font-body text-sm text-white/70 mt-1">{label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* ── Right column (illustration) ──────────────────────────────── */}
          <motion.div
            className="lg:col-span-2 flex justify-center lg:justify-end order-first lg:order-last"
            initial={{ opacity: 0, scale: 0.85, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: 'easeOut' }}
          >
            <div className="relative w-72 h-72 md:w-96 md:h-96 lg:w-full lg:max-w-sm lg:aspect-square">
              {/* Decorative ring */}
              <div className="absolute inset-0 rounded-full bg-white/10 animate-pulse-slow" />

              {/* Illustration */}
              <motion.div
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="relative z-10 w-full h-full"
              >
                <img
                  src="/assets/mouse-hero.png"
                  alt="Farafeero mascot with birthday balloons"
                  className="w-full h-full object-contain drop-shadow-2xl"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling.style.display = 'flex'
                  }}
                />
                {/* Fallback placeholder */}
                <div
                  className="hidden w-full h-full rounded-full bg-white/20 items-center justify-center text-8xl"
                  aria-hidden="true"
                >
                  🐭🎉
                </div>
              </motion.div>

              {/* Small floating emoji decorations around the image */}
              {[
                { emoji: '🎈', top: '5%',  right: '5%',  delay: 0.2 },
                { emoji: '⭐', top: '20%', left: '2%',   delay: 0.6 },
                { emoji: '🎂', bottom: '10%', right: '0%', delay: 0.4 },
                { emoji: '🎀', bottom: '25%', left: '5%',  delay: 0.8 },
              ].map(({ emoji, delay, ...pos }) => (
                <motion.span
                  key={emoji}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + delay, type: 'spring' }}
                  className="absolute text-3xl pointer-events-none select-none animate-float"
                  style={{ ...pos, animationDelay: `${delay}s` }}
                >
                  {emoji}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        onClick={() => scrollToId('services-section')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 hover:text-white/80 transition-colors cursor-pointer"
        aria-label="Scroll down"
      >
        <span className="font-body text-xs tracking-widest">SCROLL</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-0.5 h-8 bg-gradient-to-b from-white/50 to-transparent rounded-full"
        />
      </motion.button>
    </section>
  )
}
